import { Loader2, Sparkles, Download, Coins, ImagePlus, Lock } from "lucide-react";
import { BeforeAfter } from "./BeforeAfter";
import { API } from "@/lib/api";
import { scrollToId } from "@/lib/scroll";

const ACTION = { background: "Swap it", person: "Swap it", generate: "Generate", video: "Swap video" };
const VIDEO_COST = 10;

const LowCredits = ({ credits }) => (
  <div data-testid="studio-video-low-credits" className="mb-4 rounded-xl border border-white/15 bg-white/[0.04] p-4 flex gap-3 items-start">
    <Lock className="h-4 w-4 mt-0.5 shrink-0 text-[#d5d9df]" />
    <div className="text-sm">
      <div className="font-semibold text-white">Video Swap needs {VIDEO_COST} credits</div>
      <div className="text-slate-300 mt-1">You have {credits}. Add {VIDEO_COST - credits} more with a plan or credit pack to unlock videos.</div>
      <button onClick={() => scrollToId("pricing")} data-testid="studio-video-get-credits" className="mt-3 btn-line px-4 py-2 text-xs">Get credits</button>
    </div>
  </div>
);

const Stage = ({ preview, clipPreview, result, url, busy, mode }) => (
  <div className="relative aspect-[4/3] rounded-xl overflow-hidden ring-1 ring-[#d5d9df]/30 mb-6 bg-[#050505]">
    {url && result.mode === "video" ? (
      <video src={url} controls autoPlay loop muted playsInline className="absolute inset-0 h-full w-full object-cover" data-testid="studio-result-video" />
    ) : mode === "video" && clipPreview ? (
      <video src={clipPreview} muted loop autoPlay playsInline className="absolute inset-0 h-full w-full object-cover" />
    ) : url && result.before ? (
      <BeforeAfter before={result.before} after={url} alt="Your result" testId="studio-result-slider" className="absolute inset-0" labels={["Original", "Result"]} />
    ) : url ? (
      <img src={url} alt="Generated scene" className="absolute inset-0 h-full w-full object-cover" data-testid="studio-result-image" />
    ) : preview ? (
      <img src={preview} alt="Your upload" className="h-full w-full object-cover" data-testid="studio-preview-image" />
    ) : (
      <div className="h-full grid place-items-center text-slate-400 text-sm">Your result appears here</div>
    )}
    {busy && (
      <div className="absolute inset-0 bg-black/50 grid place-items-center" data-testid="studio-loading">
        <div className="scan-line absolute inset-x-0 h-24 bg-gradient-to-b from-transparent via-[#8b929c]/40 to-transparent" />
        <div className="glass rounded-full px-5 py-3 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin text-[#d5d9df]" /> {mode === "video" ? "Rendering your video… a few minutes" : "Shifting your scene…"}</div>
      </div>
    )}
  </div>
);

export const StudioResult = ({ mode, user, preview, clipPreview, result, busy, onRun, onUseAsBackground }) => {
  const url = result?.id ? `${API}/my/orders/${result.id}/result` : null;
  const free = user?.free_left ?? 0;
  const cost = mode === "video" ? VIDEO_COST : 1;
  const price = user?.unlimited ? "Unlimited" : free > 0 ? `Free (${free} left)` : `${cost} credit${cost > 1 ? "s" : ""}`;
  const balance = user?.unlimited ? "Unlimited credits" : `${user?.credits ?? 0} credits${free > 0 ? ` · ${free} free` : ""}`;
  return (
    <div className="lg:sticky lg:top-32 rounded-2xl panel p-5 sm:p-6 lg:p-8 shadow-[0_30px_100px_-30px_rgba(190,197,208,0.5)]" data-testid="studio-panel">
      <div className="flex items-center justify-between mb-6">
        <div className="eyebrow !text-[11px]">Result</div>
        <div className="flex items-center gap-2 text-sm" data-testid="studio-credits">
          <Coins className="h-4 w-4 text-[#d5d9df]" /> {user ? balance : "Sign in to see credits"}
        </div>
      </div>
      <Stage preview={preview} clipPreview={clipPreview} result={result} url={url} busy={busy} mode={mode} />
      {mode === "video" && user && !user.unlimited && free < 1 && (user.credits ?? 0) < VIDEO_COST && !busy && <LowCredits credits={user.credits ?? 0} />}
      <button onClick={onRun} disabled={busy} data-testid="studio-generate-button" className="btn-solid w-full py-5 text-sm">
        {busy ? <><Loader2 className="h-5 w-5 animate-spin" /> Working…</> : <><Sparkles className="h-5 w-5" /> {user ? `${ACTION[mode]} · ${price}` : `Sign in & ${ACTION[mode].toLowerCase()}`}</>}
      </button>
      {url && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
          <a href={url} download data-testid="studio-download-button" className={`btn-line py-3.5 ${result.mode === "generate" ? "" : "sm:col-span-2"}`}><Download className="h-4 w-4" /> Download</a>
          {result.mode === "generate" && (
            <button onClick={onUseAsBackground} data-testid="studio-use-as-background" className="btn-line py-3.5"><ImagePlus className="h-4 w-4" /> Use as background</button>
          )}
        </div>
      )}
      <button onClick={() => scrollToId("pricing")} data-testid="studio-get-credits" className="mt-4 w-full text-center text-xs text-slate-400 hover:text-white">Need credits? See plans</button>
    </div>
  );
};
